/**
 * The destructive and consequential verbs, and what each one asks before it
 * runs. `prompt()` / `confirm()` / `alert()` are out of the primary flows
 * (REDESIGN-2 §6): a verb opens a field in place, and the confirm button in that
 * field is the only thing that writes.
 *
 * The questions are the server-rendered dashboard's, word for word, because they
 * are the copy that was reviewed and because two surfaces asking the same
 * question differently is how a team learns to distrust both.
 */
export type Verb =
  | 'approve'
  | 'approveAdded'
  | 'pause'
  | 'pauseBrief'
  | 'pauseSchedule'
  | 'delete-schedule'
  | 'markMerged'
  /** The same verb again, after the hub reported that GitHub disagrees (issue #125). */
  | 'markMergedAnyway'
  /** Files an urgent fix task on the same branch; not a retry of this one. */
  | 'fixPr'
  | 'rebaseAndFix'
  | 'reject'
  | 'changes'
  | 'reassign'
  | 'cancel'
  | 'cancelBrief'
  | 'revoke'
  | 'limits'
  | 'promote'
  | 'demote'
  | 'remove-member'
  | 'set-password'
  | 'retire-worker'
  | 'disable-repo';

export interface AskConfig {
  question: string;
  confirm: string;
  /** Placeholder for the reason field. Absent means the verb asks nothing. */
  field?: string;
  /**
   * The field cannot be left empty, because the hub answers 422 without it: the
   * confirm button says no here rather than letting the write fail at the server.
   * `changes` sets it because its note is folded into the next agent run's spec;
   * `cancelBrief` because the note is the only record of why work that was
   * already running on somebody's laptop was stopped.
   */
  required?: boolean;
  numeric?: boolean;
  tone?: 'gate' | 'danger' | 'primary';
  /**
   * A verb with two ways of doing it, chosen in the box before confirming. The
   * first is the default. Pause is the one that has it: checkpoint now, or let
   * this attempt finish and then hold (docs/JOB-LIFECYCLE.md 2.5, decision 1).
   */
  choices?: readonly { value: string; label: string; hint: string }[];
}

/** Pause's two choices, in the order the design gives them. */
export const PAUSE_CHOICES = [
  {
    value: 'checkpoint',
    label: 'Checkpoint now',
    hint: 'The device pushes what it has and stops within a heartbeat. The work stays on its branch.',
  },
  {
    value: 'finish',
    label: 'Let this attempt finish',
    hint: 'The current run ends on its own. Nothing new starts until you resume.',
  },
] as const;

/**
 * What asking for a plan actually does, shared by every surface that says it.
 *
 * The request queues a planning task; it does not start one. "A worker is
 * drafting the plan" was a claim about a machine that has not been offered the
 * task yet, and the sweep interval is the difference.
 */
export const PLAN_QUEUED_NOTE = 'A worker picks it up on the next sweep.';

export const ASK: Record<Verb, AskConfig> = {
  approve: {
    question: 'Approve and dispatch this plan?',
    confirm: 'Approve',
    tone: 'gate',
  },
  /** The gate again, for the steps an amendment added. The rest of the plan is already running. */
  approveAdded: {
    question: 'Approve the added tasks? They join the queue; the rest of the job is unchanged.',
    confirm: 'Approve added tasks',
    tone: 'gate',
  },
  /**
   * Pause keeps the work: a running step pushes what it has before it stops,
   * and Resume continues from that branch. The two choices are the design's.
   */
  pause: {
    question: 'Pause this task? The work is kept and Resume continues from it.',
    confirm: 'Pause',
    field: 'why (optional)',
    choices: PAUSE_CHOICES,
  },
  pauseBrief: {
    question: 'Pause every unfinished task of this job? The work is kept and Resume continues it.',
    confirm: 'Pause job',
    field: 'why (optional)',
    choices: PAUSE_CHOICES,
  },
  /**
   * A schedule's pause has no two ways of doing it: a schedule is a clock, and
   * stopping a clock stops the next firing. Whatever job it already filed carries
   * on and is paused, if you want it paused, on its own page.
   */
  pauseSchedule: {
    question:
      'Pause this schedule? Nothing fires until you resume it. Jobs it already filed carry on.',
    confirm: 'Pause schedule',
    field: 'why (optional)',
  },
  /**
   * Deleting is the only schedule verb with no undo, and what a person fears at
   * this button is losing the work rather than the clock, so the question says
   * what survives it.
   */
  'delete-schedule': {
    question:
      'Delete this schedule? It stops firing. The jobs it already filed, and their pull requests, stay.',
    confirm: 'Delete schedule',
    tone: 'danger',
  },
  /**
   * Closing the PR gate used to be the one consequential verb in the console
   * that fired on the first press: no question, no undo, and nothing checked.
   * The hub does not talk to GitHub, so "merged" is a person's word for it and
   * the record simply believes them - which is exactly why it should be a
   * sentence somebody read rather than a button somebody brushed on a phone.
   *
   * This is the question for a step with nothing left to wait on. One with an
   * unmerged dependency gets a sharper one, built at the button (issue #24):
   * a PR to name is a fact about the task, not about the verb, so `AskBox`
   * takes `AskState.question` as an override rather than this record trying to
   * hold every task's dependency in one static sentence.
   */
  markMerged: {
    // The hub does now ask GitHub, at the moment of the click, and refuses with
    // a sentence if GitHub disagrees (issue #125). It still does not *merge*
    // anything, which is what this question has always been about: "merged" is
    // a person's word for something they did, and the record believes them.
    question: 'Mark this PR merged? The hub cannot merge; this records that you did.',
    confirm: 'Mark merged',
    tone: 'gate',
  },
  /** The same verb, after the hub said GitHub disagrees and the person decided they know better. */
  markMergedAnyway: {
    question:
      'Record the merge anyway? GitHub says this pull request is not in a mergeable state right now.',
    confirm: 'Record it anyway',
    tone: 'danger',
  },
  /**
   * Fix the PR, and its conflict-shaped sibling (issue #125).
   *
   * Both file a NEW urgent task on the same job rather than retrying this one,
   * and the question says so, because the distinction is the whole point and is
   * the thing most likely to be misread: this task succeeded, its attempts are
   * spent and its record says it passed. The new task resumes the same branch,
   * so the fix lands in the pull request already open rather than in a second
   * one.
   */
  fixPr: {
    question:
      'File an urgent task to fix this? It resumes the same branch and pushes to the same pull request. This task keeps its record and its attempts.',
    confirm: 'Fix the PR',
    tone: 'gate',
  },
  rebaseAndFix: {
    question:
      'File an urgent task to rebase and fix this? It resumes the same branch and pushes to the same pull request. This task keeps its record and its attempts.',
    confirm: 'Rebase and fix',
    tone: 'gate',
  },
  reject: {
    question: 'Why is this plan rejected?',
    confirm: 'Reject',
    field: 'reason (optional)',
    tone: 'danger',
  },
  changes: {
    question: 'What needs changing? The next agent run is told this.',
    confirm: 'Send back',
    field: 'what to change',
    required: true,
  },
  reassign: {
    question: 'Anything the next device should know?',
    confirm: 'Reassign',
    field: 'note (optional)',
  },
  cancel: {
    question: 'Cancel this task? If it is running, the laptop stops it within a heartbeat.',
    confirm: 'Cancel task',
    field: 'reason (optional)',
    tone: 'danger',
  },
  cancelBrief: {
    // What it really does. A run already on a laptop is told on that device's
    // next heartbeat and stops there (docs/JOB-LIFECYCLE.md section 2.3); the
    // copy says "within a heartbeat" rather than "now" because that is the truth.
    question:
      'Stops every unfinished task and closes the job. A task already running on a laptop is stopped within a heartbeat.',
    confirm: 'Cancel job',
    field: 'why the work is stopping',
    required: true,
    tone: 'danger',
  },
  revoke: { question: 'Revoke this invite?', confirm: 'Revoke', tone: 'danger' },
  /**
   * Promoting is a gate rather than a danger: it grants the fleet, the people
   * list and the invites, all of which the server now enforces, so it is a
   * decision about a person rather than damage to one.
   */
  promote: {
    question: 'Make this person an admin? They get the fleet, People and invites.',
    confirm: 'Make admin',
    tone: 'gate',
  },
  demote: {
    question: 'Remove admin from this person? They keep their own devices and jobs.',
    confirm: 'Remove admin',
    tone: 'danger',
  },
  'remove-member': {
    question: 'Remove this member from the hub? Their jobs and journal lines stay.',
    confirm: 'Remove member',
    tone: 'danger',
  },
  /**
   * The recovery path for a lost password, since the hub cannot send email. It
   * signs the person out everywhere and replaces what they had, so it asks; the
   * password itself appears once, in the box that asked, and is never journalled.
   */
  'set-password': {
    question:
      'Set this person a temporary password? Their current one stops working, every session of theirs is signed out, and they must change it when they next sign in.',
    confirm: 'Set temporary password',
    tone: 'danger',
  },
  /**
   * Retiring a device is the only verb that revokes a credential, and there is
   * no undo: the laptop has to redeem a fresh invite to come back. The question
   * says what survives it, because the fear at this button is losing the record
   * rather than losing the machine.
   */
  'retire-worker': {
    question: 'Retire this device? Its token stops working. Its finished work and journal stay.',
    confirm: 'Retire device',
    tone: 'danger',
  },
  /**
   * Disabling a repo is not a delete: jobs that already name it keep running,
   * and re-enabling puts it back. The question still asks, because it stops
   * the job composer from offering something a device is being wound down for.
   */
  'disable-repo': {
    question:
      "Take this repo out of the job composer's picker? Jobs that already name it still run, and re-enabling puts it back.",
    confirm: 'Disable repo',
    tone: 'danger',
  },
  limits: {
    question: 'Concurrent task limit',
    confirm: 'Save',
    field: '4',
    numeric: true,
  },
};
